import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  accountLogin,
  getListCompany,
  type CompanyItem,
} from "../../services/authServices";
import { paths } from "../../routes/paths";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(paths.home, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const resetCompanySelection = () => {
    setCompanies([]);
    setSelectedCompanyId(null);
  };

  const handleLoginWithCompany = async (companyId: number, company?: CompanyItem) => {
    setLoading(true);
    setError(null);

    try {
      const data = await accountLogin({
        company_id: companyId,
        phone_number: phoneNumber,
        password,
      });

      await login({
        companyId,
        token: data.token,
        user: data.user || { company },
      });

      const from = (location.state as { from?: string } | null)?.from;
      navigate(from || paths.home, { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Đăng nhập thất bại, vui lòng thử lại";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!phoneNumber || !password) {
      setError("Vui lòng nhập đầy đủ số điện thoại và mật khẩu");
      return;
    }

    setLoading(true);
    try {
      const list = await getListCompany({
        phone_number: phoneNumber,
        password,
      });

      if (!list.length) {
        setError("Không tìm thấy công ty nào cho tài khoản này");
        return;
      }

      if (list.length === 1) {
        await handleLoginWithCompany(list[0].id, list[0]);
        return;
      }

      setCompanies(list);
      setSelectedCompanyId(list[0]?.id ?? null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Có lỗi xảy ra, vui lòng thử lại";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedCompanyId) {
      setError("Vui lòng chọn một công ty");
      return;
    }

    const company = companies.find((item) => item.id === selectedCompanyId);
    await handleLoginWithCompany(selectedCompanyId, company);
  };

  return (
    <div className="assistant-admin-shell assistant-admin-auth-page">
      <div className="assistant-admin-aurora assistant-admin-aurora--one" />
      <div className="assistant-admin-aurora assistant-admin-aurora--two" />
      <div className="assistant-admin-grid" />

      <div className="assistant-admin-auth-card">
        <div className="assistant-admin-auth-badge">Assistant Admin</div>
        <h1 className="assistant-admin-auth-title">Đăng nhập hệ thống</h1>
        <p className="assistant-admin-auth-subtitle">
          Dùng cùng flow đăng nhập với hệ thống assistants để xác minh tài khoản trước khi vào
          dashboard quản trị.
        </p>

        <form className="assistant-admin-auth-form" onSubmit={handleSubmit}>
          <label className="assistant-admin-auth-field">
            <span>Số điện thoại</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(event) => {
                setPhoneNumber(event.target.value);
                resetCompanySelection();
              }}
              placeholder="Nhập số điện thoại"
              autoComplete="username"
            />
          </label>

          <label className="assistant-admin-auth-field">
            <span>Mật khẩu</span>
            <input
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                resetCompanySelection();
              }}
              placeholder="Nhập mật khẩu"
              autoComplete="current-password"
            />
          </label>

          {error ? <div className="assistant-admin-auth-error">{error}</div> : null}

          <button type="submit" className="assistant-admin-auth-submit" disabled={loading}>
            {loading ? "Đang xử lý..." : "Đăng nhập"}
          </button>
        </form>

        {companies.length > 1 ? (
          <form className="assistant-admin-company-card" onSubmit={handleCompanySubmit}>
            <div className="assistant-admin-company-title">Chọn công ty để tiếp tục</div>
            <p className="assistant-admin-company-subtitle">
              Tài khoản này đang liên kết với nhiều công ty. Hãy chọn đúng công ty trước khi đăng
              nhập.
            </p>

            <label className="assistant-admin-auth-field">
              <span>Công ty</span>
              <select
                value={selectedCompanyId ?? ""}
                onChange={(event) => setSelectedCompanyId(Number(event.target.value))}
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>

            <button type="submit" className="assistant-admin-auth-submit" disabled={loading}>
              {loading ? "Đang đăng nhập..." : "Đăng nhập với công ty đã chọn"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
